"use client";

import { useEffect, useState, type ReactNode } from "react";
import { WebphoneProvider } from "@/hooks/voice/webphone-provider";
import { WebphoneSticky } from "@/components/voice/webphone/webphone-sticky";
import {
  getVoiceEnabled,
  PERMISSIONS_UPDATED_EVENT,
  AUTH_CLEARED_EVENT,
} from "@/lib/permissions";

/**
 * Mantém o ramal do navegador vivo acima dos grupos de rota (#331).
 *
 * Antes, `WebphoneProvider` + `WebphoneSticky` viviam em
 * `app/dashboard/layout.tsx`. O wizard de canais (`/canais/novo/*`) fica num
 * grupo de rota irmão, fora daquele layout — então navegar do painel para lá
 * desmontava o provider inteiro.
 *
 * E o teardown SIP **não** roda no unmount, de propósito: o Strict Mode faz
 * mount→unmount→mount em <100ms, e desregistrar aí deixaria a UI fora de sync
 * com o motor durante uma chamada (ver `use-webphone.ts`). O resultado era um
 * ramal fantasma — registrado no Asterisk, recebendo INVITE, sem nenhuma tela
 * para atender. A ligação tocava os 30s inteiros e o histórico gravava
 * `NO_ANSWER`, indistinguível de atendente negligente.
 *
 * Pior, o fluxo se contradizia: conectar canal Meta **exige** estar em
 * `/canais/novo/conectar`, e o passo seguinte é receber a ligação de
 * verificação da Meta — no ramal que aquela mesma página acabava de derrubar.
 * Foi assim que a MinasBR perdeu a verificação em 05/08/2026.
 *
 * Vivendo no layout raiz, o provider sobrevive à navegação entre grupos, hoje
 * e para grupos que venham a existir. Sem re-registro SIP no caminho.
 */
export function VoiceShell({ children }: { children: ReactNode }) {
  // `getVoiceEnabled` lê localStorage; no servidor não existe. Começar em
  // `false` e decidir depois da montagem evita divergência de hidratação — o
  // layout do dashboard fazia a leitura direto no render e só não quebrava
  // porque já era client component montado depois do login.
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  useEffect(() => {
    const sync = () => setVoiceEnabled(getVoiceEnabled());
    sync();
    // A flag chega junto com as permissões, que são atualizadas ao vivo
    // (`useLivePermissions`). Sem ouvir o evento, quem habilita voz para um
    // agente logado só veria o ramal no próximo F5.
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, sync);
    // #422: e tambem o fim de sessao — o ramal tem de sumir no logout, senao a
    // bolha fica sobre a tela de login ate um reload.
    window.addEventListener(AUTH_CLEARED_EVENT, sync);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, sync);
      window.removeEventListener(AUTH_CLEARED_EVENT, sync);
    };
  }, []);

  // O wrapper de `children` é SEMPRE o mesmo tipo — só o sticky é condicional.
  //
  // A primeira versão fazia `if (!voiceEnabled) return <>{children}</>`, e como
  // `voiceEnabled` só vira `true` depois do efeito, a posição que hospeda
  // `children` trocava de Fragment para WebphoneProvider entre o 1º render e o
  // 2º. React não reaproveita subárvore quando o tipo da posição muda — e como
  // isto vive no layout RAIZ, o que remontava era a APLICAÇÃO INTEIRA, a cada
  // carregamento de página em tenant com voz: socket do chat conectando,
  // desconectando e reconectando, presença idem, requisições em dobro, state
  // zerado. Invisível em dev, porque o Strict Mode já duplica mount.
  //
  // Montar `WebphoneProvider` sempre é inerte: `useWebphone()` tem dois efeitos
  // (sincronizar um ref e registrar `pagehide`/`beforeunload`) e não abre nada.
  // Quem chama `connect()` é o sticky (`webphone-sticky.tsx:160`), atrás de
  // `hasPerms` — sem sticky, não há registro SIP.
  return (
    <WebphoneProvider>
      {children}
      {voiceEnabled ? <WebphoneSticky /> : null}
    </WebphoneProvider>
  );
}
